// SEO — GSC + auditoria HTML. Conecta o diagnóstico estratégico ao dado vivo:
// hipótese §4.1 (redesign derrubou SEO) → tendência mensal 12m com cliff visível.
// Hipótese §4.4 (H2 envolvendo texto corrido) → auditoria HTML mostra max_length.
import { useEffect, useState } from "react";
import { api } from "../../api.ts";
import { LineChart, DonutChart, HorizontalBar } from "../../components/charts/index.ts";
import { fmtNum } from "../../lib/format.ts";

interface Summary {
  clicks: number; impressions: number; ctr: number;
  avg_position: number; unique_queries: number;
}
interface QueryRow { query: string; clicks: number; impressions: number; ctr: number; position: number }
interface PageRow { page: string; clicks: number; impressions: number; ctr: number; position: number }
interface SeriesRow { date: string; clicks: number; impressions: number }
interface MonthlyRow { month: string; clicks: number; impressions: number; avg_position: number }
interface DeviceRow { device: string; clicks: number; impressions: number; ctr: number }
interface CountryRow { country: string; clicks: number; impressions: number }
interface BrandRow { kind: "brand" | "non_brand"; clicks: number; impressions: number }
interface PageChange { page: string; current_clicks: number; prev_clicks: number; delta: number }
interface Audit {
  url: string; kind: string; fetched_at: string; http_status: number;
  title: string | null; title_length: number | null; meta_description_length: number | null;
  canonical: string | null; robots: string | null;
  h1_count: number; h2_count: number; h2_max_length: number | null; h2_long_count: number;
  img_total: number; img_without_alt: number;
  has_jsonld: boolean; jsonld_types: string[];
  og_complete: boolean; has_breadcrumb: boolean;
  findings: string[];
}

interface WindowMetrics {
  cur_clicks: number; cur_imp: number; cur_pos: number;
  prev_clicks: number; prev_imp: number; prev_pos: number;
}
interface Health {
  checked_at: string;
  googlebot_can_access: boolean;
  robots_changed: boolean | null;
  robots_baseline_date: string | null;
  robots_current?: string | null;
  robots_baseline?: string | null;
  home_status_browser: number | null;
  home_status_googlebot: number | null;
  sitemap_status: number | null;
  sitemap_url_count: number;
  sitemap_sub_count: number;
  findings: string[];
  home_response_headers: Record<string, string>;
}
interface HealthHistoryRow {
  date: string;
  googlebot_can_access: boolean;
  home_status_googlebot: number | null;
  sitemap_url_count: number;
  robots_changed: boolean | null;
  finding_count: number;
}

interface Payload {
  configured: boolean;
  summary?: Summary;
  prev_period?: { clicks: number; impressions: number };
  series_90d?: SeriesRow[];
  monthly_12m?: MonthlyRow[];
  devices?: DeviceRow[];
  countries?: CountryRow[];
  page_winners?: PageChange[];
  page_losers?: PageChange[];
  top_queries?: QueryRow[];
  ctr_opportunities?: QueryRow[];
  top_pages?: PageRow[];
  brand_split?: BrandRow[];
  audits?: Audit[];
  windows?: { d3: WindowMetrics; m6: WindowMetrics; m12: WindowMetrics };
  health?: Health | null;
  health_history?: HealthHistoryRow[];
}

const MONTHS_PT = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];

function dayLabel(iso: string) {
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, "0")}/${MONTHS_PT[d.getUTCMonth()]}`;
}
function monthLabel(iso: string) {
  const d = new Date(iso);
  return `${MONTHS_PT[d.getUTCMonth()]}/${String(d.getUTCFullYear()).slice(-2)}`;
}
function shortPath(url: string) {
  try {
    const u = new URL(url);
    const p = u.pathname.replace(/\/$/, "") || "/";
    return p.length > 60 ? p.slice(0, 57) + "…" : p;
  } catch {
    return url.length > 60 ? url.slice(0, 57) + "…" : url;
  }
}

const DEVICE_LABEL: Record<string, string> = { desktop: "Desktop", mobile: "Mobile", tablet: "Tablet" };
const DEVICE_COLOR: Record<string, string> = { mobile: "#131313", desktop: "#ffd000", tablet: "#9aa39d" };

export function SeoPanel({ slug }: { slug: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Payload>(`/api/t/${slug}/tactical/seo`).then(setData).catch((e) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="card"><p style={{ color: "var(--red)" }}>erro: {err}</p></div>;
  if (!data) return <div className="center-loading">carregando…</div>;
  if (!data.configured) {
    return (
      <div className="card">
        <p className="eyebrow">SEO · aguardando primeiro sync</p>
        <h3 style={{ marginTop: ".25rem" }}>Google Search Console conectado, sem dados ainda</h3>
        <p style={{ color: "var(--text-soft)", fontSize: ".9rem", marginTop: ".5rem" }}>
          Volte em alguns minutos — backfill em andamento.
        </p>
      </div>
    );
  }

  const s = data.summary!;
  const p = data.prev_period!;
  const clicksDelta = p.clicks > 0 ? ((s.clicks - p.clicks) / p.clicks) * 100 : null;
  const impressionsDelta = p.impressions > 0 ? ((s.impressions - p.impressions) / p.impressions) * 100 : null;

  const series = (data.series_90d ?? []).map((r) => ({ label: dayLabel(r.date), value: r.clicks }));
  const monthlyClicks = (data.monthly_12m ?? []).map((r) => ({
    label: r.month.startsWith(new Date().toISOString().slice(0, 7)) ? `${monthLabel(r.month)} (parcial)` : monthLabel(r.month),
    value: r.clicks,
  }));
  const monthlyPos = (data.monthly_12m ?? [])
    .filter((r) => !r.month.startsWith(new Date().toISOString().slice(0, 7)) || r.avg_position > 0)
    .map((r) => ({ label: monthLabel(r.month), value: r.avg_position }));

  const topQueriesBar = (data.top_queries ?? []).slice(0, 10).map((q) => ({
    name: q.query.length > 42 ? q.query.slice(0, 39) + "…" : q.query,
    value: q.clicks,
  }));

  const brandSlices = (data.brand_split ?? []).map((b) => ({
    name: b.kind === "brand" ? "Brand (nerea)" : "Não-brand",
    value: b.clicks,
    color: b.kind === "brand" ? "#ffd000" : "#131313",
  })).filter((b) => b.value > 0);
  const brandTotal = brandSlices.reduce((acc, c) => acc + c.value, 0);

  const deviceSlices = (data.devices ?? [])
    .filter((d) => d.clicks > 0)
    .map((d) => ({
      name: DEVICE_LABEL[d.device] ?? d.device,
      value: d.clicks,
      color: DEVICE_COLOR[d.device],
    }));
  const deviceTotal = deviceSlices.reduce((s, c) => s + c.value, 0);

  // Cliff detection — ignora o mês corrente (sempre parcial). Detecta maior
  // queda em IMPRESSÕES (sintoma de remoção de páginas do índice).
  const m12All = data.monthly_12m ?? [];
  const currentMonthIso = new Date().toISOString().slice(0, 7); // YYYY-MM
  const m12 = m12All.filter((r) => !r.month.startsWith(currentMonthIso));
  const lastIsPartial = m12All.length > m12.length;

  let cliff: { month: string; pct: number; metric: "impressions" | "clicks" } | null = null;
  for (let i = 1; i < m12.length; i++) {
    const prevI = m12[i - 1]!.impressions;
    const curI = m12[i]!.impressions;
    if (prevI >= 5000 && curI < prevI) {
      const pct = ((prevI - curI) / prevI) * 100;
      if (pct >= 40 && (!cliff || pct > cliff.pct)) cliff = { month: m12[i]!.month, pct, metric: "impressions" };
    }
  }
  if (!cliff) {
    for (let i = 1; i < m12.length; i++) {
      const prev = m12[i - 1]!.clicks;
      const cur = m12[i]!.clicks;
      if (prev >= 100 && cur < prev) {
        const pct = ((prev - cur) / prev) * 100;
        if (!cliff || pct > cliff.pct) cliff = { month: m12[i]!.month, pct, metric: "clicks" };
      }
    }
  }

  const W = data.windows;
  const H = data.health;

  function windowCard(title: string, subtitle: string, w?: WindowMetrics) {
    if (!w) return null;
    const clicksDelta = w.prev_clicks > 0 ? ((w.cur_clicks - w.prev_clicks) / w.prev_clicks) * 100 : null;
    const impDelta = w.prev_imp > 0 ? ((w.cur_imp - w.prev_imp) / w.prev_imp) * 100 : null;
    const posDelta = w.prev_pos > 0 ? (w.prev_pos - w.cur_pos) : null; // positivo = melhorou (subiu)

    const verdict = (() => {
      if (clicksDelta === null) return null;
      if (clicksDelta >= 5)  return { color: "var(--green)", text: "Crescendo" };
      if (clicksDelta <= -5) return { color: "var(--red)",   text: "Caindo" };
      return { color: "var(--text-muted)", text: "Estável" };
    })();

    return (
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
        <div>
          <p className="eyebrow" style={{ margin: 0 }}>{title}</p>
          <p style={{ margin: ".15rem 0 0", color: "var(--text-muted)", fontSize: ".78rem" }}>{subtitle}</p>
        </div>
        {verdict && (
          <div style={{ fontSize: "1.4rem", fontWeight: 700, color: verdict.color, fontFamily: "var(--f-display)" }}>
            {verdict.text}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".5rem", fontSize: ".88rem" }}>
          <div>
            <div style={{ color: "var(--text-muted)", fontSize: ".72rem", textTransform: "uppercase" }}>Cliques</div>
            <div className="mono" style={{ fontWeight: 600 }}>{fmtNum(w.cur_clicks)}</div>
            {clicksDelta !== null && (
              <div className="mono" style={{ fontSize: ".74rem", color: clicksDelta >= 0 ? "var(--green)" : "var(--red)" }}>
                {clicksDelta >= 0 ? "↑" : "↓"} {Math.abs(clicksDelta).toFixed(0)}%
              </div>
            )}
          </div>
          <div>
            <div style={{ color: "var(--text-muted)", fontSize: ".72rem", textTransform: "uppercase" }}>Impressões</div>
            <div className="mono" style={{ fontWeight: 600 }}>{fmtNum(w.cur_imp)}</div>
            {impDelta !== null && (
              <div className="mono" style={{ fontSize: ".74rem", color: impDelta >= 0 ? "var(--green)" : "var(--red)" }}>
                {impDelta >= 0 ? "↑" : "↓"} {Math.abs(impDelta).toFixed(0)}%
              </div>
            )}
          </div>
          <div>
            <div style={{ color: "var(--text-muted)", fontSize: ".72rem", textTransform: "uppercase" }}>Posição</div>
            <div className="mono" style={{ fontWeight: 600 }}>{w.cur_pos.toFixed(1)}</div>
            {posDelta !== null && Math.abs(posDelta) >= 0.05 && (
              <div className="mono" style={{ fontSize: ".74rem", color: posDelta >= 0 ? "var(--green)" : "var(--red)" }}>
                {posDelta >= 0 ? "↑ subiu" : "↓ caiu"} {Math.abs(posDelta).toFixed(1)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {W && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Evolução · 3 janelas</p>
              <h2>Como está o SEO comparado ao período anterior</h2>
            </div>
          </div>
          <div className="grid g3">
            {windowCard("12 meses", "vs 12 meses anteriores", W.m12)}
            {windowCard("6 meses",  "vs 6 meses anteriores",  W.m6)}
            {windowCard("3 dias",   "vs 3 dias anteriores",   W.d3)}
          </div>
        </div>
      )}

      {H && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Saúde técnica · check diário automático</p>
              <h2>O Google consegue acessar o site?</h2>
            </div>
          </div>
          <div className="card" style={{
            borderLeft: `4px solid ${H.googlebot_can_access ? "var(--green)" : "var(--red)"}`,
          }}>
            <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", alignItems: "flex-start" }}>
              <div>
                <div className="eyebrow" style={{ margin: 0 }}>Googlebot</div>
                <div style={{
                  fontSize: "1.4rem", fontWeight: 700,
                  color: H.googlebot_can_access ? "var(--green)" : "var(--red)",
                  fontFamily: "var(--f-display)",
                }}>
                  {H.googlebot_can_access ? "✓ Acessa OK" : "⚠ Bloqueado"}
                </div>
                <div style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>
                  HTTP {H.home_status_googlebot ?? "—"} (browser {H.home_status_browser ?? "—"})
                </div>
              </div>
              <div>
                <div className="eyebrow" style={{ margin: 0 }}>robots.txt</div>
                <div style={{
                  fontSize: "1.4rem", fontWeight: 700,
                  color: H.robots_changed ? "var(--amber)" : "var(--green)",
                  fontFamily: "var(--f-display)",
                }}>
                  {H.robots_changed === null ? "—" : H.robots_changed ? "Mudou" : "Estável"}
                </div>
                <div style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>
                  vs baseline {H.robots_baseline_date ?? "—"}
                </div>
              </div>
              <div>
                <div className="eyebrow" style={{ margin: 0 }}>Sitemap</div>
                <div style={{
                  fontSize: "1.4rem", fontWeight: 700,
                  color: H.sitemap_url_count > 0 ? "var(--green)" : "var(--red)",
                  fontFamily: "var(--f-display)",
                }}>
                  {fmtNum(H.sitemap_url_count)} URLs
                </div>
                <div style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>
                  {H.sitemap_sub_count} sub-sitemaps · HTTP {H.sitemap_status}
                </div>
              </div>
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div className="eyebrow" style={{ margin: 0 }}>Última verificação</div>
                <div className="mono" style={{ fontSize: ".88rem" }}>
                  {new Date(H.checked_at).toLocaleString("pt-BR")}
                </div>
                <div style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>
                  Reroda automaticamente às 06:00 BRT
                </div>
              </div>
            </div>
            {(H.findings ?? []).length > 0 && (
              <div style={{ marginTop: "1rem", padding: ".75rem 1rem", background: "rgba(185,28,28,.05)", borderRadius: 6 }}>
                <strong style={{ color: "var(--red)" }}>Alertas detectados:</strong>
                <ul style={{ margin: ".4rem 0 0", paddingLeft: "1.25rem" }}>
                  {H.findings.map((f, i) => (
                    <li key={i} style={{ fontSize: ".86rem", color: "var(--red)", marginBottom: ".15rem" }}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {(H.findings ?? []).length === 0 && (
              <div style={{ marginTop: "1rem", padding: ".75rem 1rem", background: "rgba(4,120,87,.05)", borderRadius: 6, color: "var(--green)" }}>
                ✓ Nenhum problema técnico detectado no último check.
              </div>
            )}
            {H.robots_changed && H.robots_current && H.robots_baseline && (() => {
              const baseLines = H.robots_baseline.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
              const curLines = H.robots_current.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
              const added = curLines.filter(l => !baseLines.includes(l));
              const removed = baseLines.filter(l => !curLines.includes(l));
              const criticalRemovals = removed.filter(l =>
                /^User-agent:\s*(Googlebot|AdsBot-Google|Mediapartners-Google)/i.test(l) ||
                /^Allow:\s*\/$/i.test(l)
              );
              return (
                <details style={{ marginTop: "1rem", fontSize: ".82rem" }} open={criticalRemovals.length > 0}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    Diff do robots.txt · {added.length} linhas adicionadas, {removed.length} removidas
                    {criticalRemovals.length > 0 && (
                      <span style={{ color: "var(--red)", marginLeft: ".5rem" }}>
                        · {criticalRemovals.length} REGRAS DE GOOGLEBOT REMOVIDAS ⚠
                      </span>
                    )}
                  </summary>
                  {criticalRemovals.length > 0 && (
                    <div style={{
                      margin: ".75rem 0", padding: ".85rem 1rem",
                      background: "rgba(185,28,28,.08)",
                      borderLeft: "3px solid var(--red)",
                    }}>
                      <strong style={{ color: "var(--red)" }}>Causa-raiz provável do cliff de ago/25:</strong> as regras
                      explícitas <code>Allow: /</code> para Googlebot/AdsBot/Mediapartners foram removidas.
                      Sem elas, o Googlebot herda das regras <code>User-agent: *</code> que ficaram muito mais restritivas
                      (novos <code>Disallow: /*?*X=</code> em qualquer posição). Isso explica a queda de impressões e a manutenção dos cliques nas páginas core.
                    </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem" }}>
                    <div>
                      <div style={{ color: "var(--green)", fontWeight: 600, marginBottom: ".25rem" }}>+ Adicionadas (hoje)</div>
                      <pre style={{ background: "rgba(4,120,87,.06)", padding: ".5rem", fontSize: ".72rem", margin: 0, maxHeight: 280, overflow: "auto" }}>
                        {added.join("\n") || "—"}
                      </pre>
                    </div>
                    <div>
                      <div style={{ color: "var(--red)", fontWeight: 600, marginBottom: ".25rem" }}>− Removidas (de jul/25)</div>
                      <pre style={{ background: "rgba(185,28,28,.06)", padding: ".5rem", fontSize: ".72rem", margin: 0, maxHeight: 280, overflow: "auto" }}>
                        {removed.join("\n") || "—"}
                      </pre>
                    </div>
                  </div>
                </details>
              );
            })()}

            {H.home_response_headers && Object.keys(H.home_response_headers).length > 0 && (
              <details style={{ marginTop: "1rem", fontSize: ".82rem" }}>
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>Headers de resposta detectados (CDN, cache)</summary>
                <pre style={{
                  margin: ".5rem 0 0", padding: ".75rem", background: "#f5f3ed",
                  borderRadius: 4, overflowX: "auto", fontSize: ".74rem",
                }}>
                  {["server","via","x-cache","x-served-by","cache-control","cf-ray","cf-cache-status","x-gocache-cachestatus"]
                    .filter((k) => H.home_response_headers[k])
                    .map((k) => `${k}: ${H.home_response_headers[k]}`).join("\n")}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="label">Cliques · 90 dias</div>
          <div className="value">{fmtNum(s.clicks)}</div>
          <div className="meta">
            <span className="mono">vs {fmtNum(p.clicks)} 90d ant.</span>
            {clicksDelta !== null && (
              <span className={`pill ${clicksDelta >= 0 ? "" : "red"}`} style={{ marginLeft: ".4rem" }}>
                {clicksDelta >= 0 ? "+" : ""}{clicksDelta.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        <div className="kpi-card">
          <div className="label">Impressões · 90 dias</div>
          <div className="value">{fmtNum(s.impressions)}</div>
          <div className="meta">
            <span className="mono">vs {fmtNum(p.impressions)} 90d ant.</span>
            {impressionsDelta !== null && (
              <span className={`pill ${impressionsDelta >= 0 ? "" : "red"}`} style={{ marginLeft: ".4rem" }}>
                {impressionsDelta >= 0 ? "+" : ""}{impressionsDelta.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        <div className="kpi-card">
          <div className="label">CTR médio</div>
          <div className="value">{s.ctr.toFixed(2)}<small>%</small></div>
          <div className="meta"><span className="mono">cliques / impressões</span></div>
        </div>
        <div className="kpi-card">
          <div className="label">Posição média</div>
          <div className="value">{s.avg_position.toFixed(1)}</div>
          <div className="meta"><span className="mono">{fmtNum(s.unique_queries)} queries únicas</span></div>
        </div>
      </div>

      {monthlyClicks.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Cliff analysis — 2 eventos distintos</p>
              <h2>Cliques orgânicos · 12 meses</h2>
            </div>
          </div>
          <div className="card">
            <LineChart
              data={monthlyClicks}
              height={280}
              formatValue={fmtNum}
              markers={[
                { label: "ago/25", text: "Cliff 1 — perda de indexação", color: "#b91c1c" },
                { label: "out/25", text: "Redesign", color: "#b45309" },
              ]}
            />
            {lastIsPartial && (
              <p style={{ marginTop: ".5rem", color: "var(--text-muted)", fontSize: ".78rem", fontStyle: "italic" }}>
                Último ponto = mês corrente em andamento (parcial — não comparar com meses completos).
              </p>
            )}
            <div style={{ marginTop: "1rem", padding: ".85rem 1rem", background: "rgba(185,28,28,.05)", borderLeft: "3px solid var(--red)", fontSize: ".88rem" }}>
              <strong style={{ color: "var(--red)" }}>Cliff 1 — agosto/2025:</strong> impressões caíram 77%
              (512k → 115k), atingindo todos os devices proporcionalmente e todos os tipos de página
              (blog, categoria, produto). <strong>Não é redesign.</strong> Hipóteses: GoCache bloqueando Googlebot,
              robots.txt mudou, problema de sitemap, ou Core Update do Google.
            </div>
            <div style={{ marginTop: ".5rem", padding: ".85rem 1rem", background: "rgba(180,83,9,.07)", borderLeft: "3px solid var(--amber)", fontSize: ".88rem" }}>
              <strong style={{ color: "var(--amber)" }}>Cliff 2 — final de outubro/2025 (redesign):</strong> impressões já estavam baixas,
              mas cliques caíram 17% em nov (1.651 → 1.367) e 28% em dez (981). Coincide com o lançamento
              do template novo. Auditoria HTML confirma: H2 com 1.300+ chars nas categorias derruba CTR e
              relevância — <strong>§4.4 do diagnóstico</strong>.
            </div>
          </div>
        </div>
      )}

      {monthlyPos.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Posição média mês-a-mês</p>
              <h2>Tendência de posição (menor = melhor)</h2>
            </div>
          </div>
          <div className="card">
            <LineChart data={monthlyPos} height={220} formatValue={(v) => v.toFixed(1)} color="#b45309" />
          </div>
        </div>
      )}

      {series.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Tendência diária</p>
              <h2>Cliques orgânicos · 90 dias</h2>
            </div>
          </div>
          <div className="card">
            <LineChart data={series} height={220} formatValue={fmtNum} showDots={false} />
          </div>
        </div>
      )}

      {(data.audits ?? []).length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Auditoria HTML ao vivo</p>
              <h2>Análise técnica de páginas-chave</h2>
            </div>
          </div>
          <div className="card">
            <p style={{ marginTop: 0, color: "var(--text-soft)", fontSize: ".88rem" }}>
              Snapshot das páginas que mais geram tráfego. Cada finding marcado abaixo é uma falha
              técnica que afeta indexação ou ranking. Rodar <code>bun scripts/seo_audit.ts</code> atualiza.
            </p>
            <table className="simple">
              <thead>
                <tr>
                  <th>Página</th>
                  <th style={{ textAlign: "right" }}>H1</th>
                  <th style={{ textAlign: "right" }}>H2 max chars</th>
                  <th style={{ textAlign: "right" }}>img s/alt</th>
                  <th>Schema</th>
                  <th style={{ textAlign: "right" }}>Findings</th>
                </tr>
              </thead>
              <tbody>
                {data.audits!.map((a, i) => {
                  const isCritical = (a.h2_long_count > 0) || (a.h1_count !== 1);
                  return (
                    <tr key={i} style={isCritical ? { background: "rgba(185,28,28,.04)" } : undefined}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{a.kind}</div>
                        <a href={a.url} target="_blank" rel="noopener" className="mono" style={{ fontSize: ".78rem" }}>
                          {shortPath(a.url)}
                        </a>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">
                        <span style={{ color: a.h1_count !== 1 ? "var(--red)" : "var(--text)" }}>{a.h1_count}</span>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">
                        <span style={{
                          color: (a.h2_max_length ?? 0) > 300 ? "var(--red)"
                               : (a.h2_max_length ?? 0) > 150 ? "var(--amber)" : "var(--text)",
                          fontWeight: (a.h2_max_length ?? 0) > 300 ? 700 : 400,
                        }}>
                          {fmtNum(a.h2_max_length ?? 0)}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">
                        {a.img_without_alt}/{a.img_total}
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: ".74rem" }}>
                          {a.jsonld_types.length > 0 ? a.jsonld_types.join(", ") : <span style={{ color: "var(--red)" }}>—</span>}
                          {!a.has_breadcrumb && <span style={{ color: "var(--amber)" }}> · sem breadcrumb</span>}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">
                        <span className={`pill ${a.findings.length >= 4 ? "red" : a.findings.length > 0 ? "amber" : ""}`}>
                          {a.findings.length}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <details style={{ marginTop: "1.25rem" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>Ver findings detalhados</summary>
              {data.audits!.map((a, i) => (
                <div key={i} style={{
                  margin: "1rem 0 .5rem", padding: ".75rem 1rem",
                  background: "#f5f3ed", borderRadius: 6,
                }}>
                  <div style={{ fontWeight: 600, fontSize: ".88rem" }}>
                    <span style={{ textTransform: "uppercase", marginRight: ".5rem" }}>{a.kind}</span>
                    <a href={a.url} target="_blank" rel="noopener" className="mono" style={{ fontSize: ".82rem" }}>{shortPath(a.url)}</a>
                  </div>
                  {a.title && (
                    <div style={{ fontSize: ".82rem", color: "var(--text-soft)", marginTop: ".25rem" }}>
                      <strong>title ({a.title_length}):</strong> {a.title}
                    </div>
                  )}
                  {a.findings.length === 0 ? (
                    <div style={{ color: "var(--green)", fontSize: ".82rem", marginTop: ".25rem" }}>✓ sem problemas</div>
                  ) : (
                    <ul style={{ margin: ".4rem 0 0", padding: 0, listStyle: "none" }}>
                      {a.findings.map((f, j) => (
                        <li key={j} style={{ fontSize: ".82rem", color: "var(--red)", marginBottom: ".15rem" }}>
                          ⚠ {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </details>
          </div>
        </div>
      )}

      {(deviceSlices.length > 0 || (data.countries ?? []).length > 0) && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Distribuição de tráfego · 90 dias</p>
              <h2>Device & país</h2>
            </div>
          </div>
          <div className="grid g2">
            {deviceSlices.length > 0 && (
              <div className="card">
                <h3>Cliques por device</h3>
                <DonutChart
                  data={deviceSlices}
                  centerLabel={{ value: fmtNum(deviceTotal), sub: "cliques 90d" }}
                  height={260}
                />
                {data.devices && (
                  <table className="simple" style={{ marginTop: ".5rem" }}>
                    <thead>
                      <tr><th>Device</th><th style={{ textAlign: "right" }}>Cliques</th><th style={{ textAlign: "right" }}>Impressões</th><th style={{ textAlign: "right" }}>CTR</th></tr>
                    </thead>
                    <tbody>
                      {data.devices.map((d, i) => (
                        <tr key={i}>
                          <td>{DEVICE_LABEL[d.device] ?? d.device}</td>
                          <td style={{ textAlign: "right" }} className="mono">{fmtNum(d.clicks)}</td>
                          <td style={{ textAlign: "right" }} className="mono">{fmtNum(d.impressions)}</td>
                          <td style={{ textAlign: "right" }} className="mono">{d.ctr.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {(data.countries ?? []).length > 0 && (
              <div className="card">
                <h3>Cliques por país</h3>
                <table className="simple">
                  <thead><tr><th>País</th><th style={{ textAlign: "right" }}>Cliques</th><th style={{ textAlign: "right" }}>Impressões</th></tr></thead>
                  <tbody>
                    {data.countries!.map((c, i) => (
                      <tr key={i}>
                        <td className="mono">{c.country}</td>
                        <td style={{ textAlign: "right" }} className="mono">{fmtNum(c.clicks)}</td>
                        <td style={{ textAlign: "right" }} className="mono">{fmtNum(c.impressions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p style={{ fontSize: ".78rem", color: "var(--text-muted)", marginTop: ".75rem" }}>
                  Quase 100% Brasil — expansão para PT/AO/MZ exigiria conteúdo localizado.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {((data.page_winners ?? []).length > 0 || (data.page_losers ?? []).length > 0) && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">90d vs 90d anteriores</p>
              <h2>Páginas em alta & em queda</h2>
            </div>
          </div>
          <div className="grid g2">
            {(data.page_winners ?? []).length > 0 && (
              <div className="card">
                <h3 style={{ color: "var(--green)" }}>↑ Vencedoras</h3>
                <table className="simple">
                  <thead><tr><th>Página</th><th style={{ textAlign: "right" }}>Δ cliques</th><th style={{ textAlign: "right" }}>Agora</th></tr></thead>
                  <tbody>
                    {data.page_winners!.map((p, i) => (
                      <tr key={i}>
                        <td>
                          <a href={p.page} target="_blank" rel="noopener" className="mono" style={{ fontSize: ".78rem" }}>
                            {shortPath(p.page)}
                          </a>
                        </td>
                        <td style={{ textAlign: "right", color: "var(--green)" }} className="mono">+{p.delta}</td>
                        <td style={{ textAlign: "right" }} className="mono">{p.current_clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(data.page_losers ?? []).length > 0 && (
              <div className="card">
                <h3 style={{ color: "var(--red)" }}>↓ Perdedoras</h3>
                <table className="simple">
                  <thead><tr><th>Página</th><th style={{ textAlign: "right" }}>Δ cliques</th><th style={{ textAlign: "right" }}>Antes</th></tr></thead>
                  <tbody>
                    {data.page_losers!.map((p, i) => (
                      <tr key={i}>
                        <td>
                          <a href={p.page} target="_blank" rel="noopener" className="mono" style={{ fontSize: ".78rem" }}>
                            {shortPath(p.page)}
                          </a>
                        </td>
                        <td style={{ textAlign: "right", color: "var(--red)" }} className="mono">{p.delta}</td>
                        <td style={{ textAlign: "right" }} className="mono">{p.prev_clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {(data.ctr_opportunities ?? []).length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Quick wins</p>
              <h2>Oportunidades de CTR (alta impressão, posição 1–15)</h2>
            </div>
          </div>
          <div className="card">
            <p style={{ color: "var(--text-soft)", fontSize: ".85rem", marginTop: 0 }}>
              Queries onde a Nerea aparece mas o clique não acontece — otimizar título e meta description pode dobrar o tráfego.
            </p>
            <table className="simple">
              <thead>
                <tr>
                  <th>Query</th>
                  <th style={{ textAlign: "right" }}>Impressões</th>
                  <th style={{ textAlign: "right" }}>Cliques</th>
                  <th style={{ textAlign: "right" }}>CTR</th>
                  <th style={{ textAlign: "right" }}>Pos</th>
                </tr>
              </thead>
              <tbody>
                {data.ctr_opportunities!.map((q, i) => (
                  <tr key={i}>
                    <td><strong>{q.query}</strong></td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtNum(q.impressions)}</td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtNum(q.clicks)}</td>
                    <td style={{ textAlign: "right" }} className="mono">
                      <span style={{ color: q.ctr < 1 ? "var(--red)" : q.ctr < 3 ? "var(--amber)" : "var(--text)" }}>
                        {q.ctr.toFixed(2)}%
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }} className="mono">{q.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {topQueriesBar.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Top 10</p>
              <h2>Queries que mais trazem clique · 90 dias</h2>
            </div>
          </div>
          <div className="grid g2">
            <div className="card">
              <HorizontalBar
                data={topQueriesBar}
                height={Math.max(220, topQueriesBar.length * 32)}
                formatValue={fmtNum}
              />
            </div>
            {brandSlices.length === 2 && (
              <div className="card">
                <h3>Brand vs não-brand</h3>
                <DonutChart
                  data={brandSlices}
                  centerLabel={{ value: fmtNum(brandTotal), sub: "cliques 90d" }}
                  height={280}
                />
                <p style={{ color: "var(--text-soft)", fontSize: ".82rem", marginTop: ".5rem" }}>
                  Não-brand mede o quanto a Nerea atrai público novo (não busca pelo nome).
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {(data.top_pages ?? []).length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Top pages</p>
              <h2>Páginas com mais cliques orgânicos · 90 dias</h2>
            </div>
          </div>
          <div className="card">
            <table className="simple">
              <thead>
                <tr>
                  <th>Página</th>
                  <th style={{ textAlign: "right" }}>Cliques</th>
                  <th style={{ textAlign: "right" }}>Impressões</th>
                  <th style={{ textAlign: "right" }}>CTR</th>
                  <th style={{ textAlign: "right" }}>Pos</th>
                </tr>
              </thead>
              <tbody>
                {data.top_pages!.map((p, i) => (
                  <tr key={i}>
                    <td>
                      <a href={p.page} target="_blank" rel="noopener" className="mono" style={{ fontSize: ".82rem" }}>
                        {shortPath(p.page)}
                      </a>
                    </td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtNum(p.clicks)}</td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtNum(p.impressions)}</td>
                    <td style={{ textAlign: "right" }} className="mono">{p.ctr.toFixed(2)}%</td>
                    <td style={{ textAlign: "right" }} className="mono">{p.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
